import DOMPurify from 'dompurify'

interface AttachmentRef {
  filename: string
  content_type: string
  size: number
  url: string
  content_id?: string | null
}

interface MessageBodyProps {
  html?: string | null
  text?: string | null
  preview?: string | null
  attachments?: AttachmentRef[]
}

const TRANSPARENT_GIF =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

function sanitizeAndHardenHtml(rawHtml: string, attachments?: AttachmentRef[]): string {
  // CHECKPOINT: PRD-10 security — Strip dangerous attributes and enforce link rel
  const clean = DOMPurify.sanitize(rawHtml, {
    USE_PROFILES: { html: true },
    ALLOWED_TAGS: [
      'p', 'br', 'hr', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'a', 'img', 'ul', 'ol', 'li',
      'blockquote', 'pre', 'code', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'sup', 'sub', 'del', 'ins',
    ],
    ALLOWED_ATTR: ['href', 'title', 'src', 'alt', 'width', 'height', 'style'],
  })

  // Build a map of Content-ID -> signed attachment URL for inline images.
  const cidUrlMap = new Map<string, string>()
  for (const att of attachments || []) {
    if (att.content_id) {
      cidUrlMap.set(String(att.content_id).replace(/[<>]/g, ''), att.url)
    }
  }

  const tmp = document.createElement('div')
  tmp.innerHTML = clean

  // Post-process anchors to open externally and prevent tabnabbing.
  tmp.querySelectorAll('a').forEach((anchor) => {
    anchor.setAttribute('rel', 'noopener noreferrer')
    anchor.setAttribute('target', '_blank')
  })

  // Block remote/tracking images and resolve cid: references to local attachments.
  tmp.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src')
    if (!src) return
    const lower = src.toLowerCase()

    if (lower.startsWith('cid:')) {
      const cid = src.slice(4).trim().replace(/[<>]/g, '')
      const url = cidUrlMap.get(cid)
      if (url) {
        img.setAttribute('src', url)
      } else {
        img.removeAttribute('src')
      }
    } else if (!lower.startsWith('data:') && !lower.startsWith('blob:')) {
      // Remote image — do not load. Keep original src for optional user approval later.
      img.setAttribute('data-src', src)
      img.setAttribute('src', TRANSPARENT_GIF)
      img.classList.add('dashboard-blocked-image')
    }
  })

  return tmp.innerHTML
}

export function MessageBody({ html, text, preview, attachments }: MessageBodyProps) {
  if (html) {
    const clean = sanitizeAndHardenHtml(html, attachments)
    return <div className="message-body prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: clean }} />
  }

  if (text) {
    return (
      <div className="message-body whitespace-pre-wrap font-sans text-sm">
        {text}
      </div>
    )
  }

  if (preview) {
    return <p className="text-sm text-muted-foreground italic">{preview}</p>
  }

  return <p className="text-sm text-muted-foreground italic">No body content available.</p>
}
